import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Divider, IconButton, MenuItem, Popover, Select, Stack, Tooltip, Typography } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  StrikethroughS,
  Link as LinkIcon,
  LinkOff,
  FormatClear,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Undo,
  Redo,
  FormatColorText,
  FormatColorFill,
  Mic,
  MicOff,
  VolumeUp,
  Stop,
  RecordVoiceOver,
} from '@mui/icons-material';
import RichTextColorButton from '../editor/RichTextColorButton';
import { useTtsVoice, speakWithVoice } from 'hooks/useTtsVoice';
import { micButtonSx, speakerButtonSx } from 'utils/speechButtonSx';

const VOICE_TEST_TEXT = 'Esta es una prueba de la voz seleccionada. Ticket número 1024, revisa la carpeta barra home barra usuario.';

const applyCommand = (command, value = null, readOnly = false) => {
  if (readOnly) return;
  document.execCommand(command, false, value);
};

const insertImage = (dataUrl, readOnly = false) => {
  if (readOnly) return;
  document.execCommand('insertImage', false, dataUrl);
};

const applyTextBackgroundColor = (color, readOnly = false) => {
  if (readOnly) return;
  try {
    document.execCommand('hiliteColor', false, color);
  } catch (error) {
    document.execCommand('backColor', false, color);
  }
};

const FONT_OPTIONS = [
  'Inter',
  'Poppins',
  'Montserrat',
  'Nunito',
  'Manrope',
  'Rubik',
  'DM Sans',
  'Open Sans',
  'Lato',
  'Roboto',
  'Arial',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Tahoma',
];

const readImage = (file, callback) => {
  const reader = new FileReader();
  reader.onload = () => callback(reader.result);
  reader.readAsDataURL(file);
};

const placeCaretAtEnd = (element) => {
  if (!element) return null;
  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  const selection = window.getSelection();
  if (!selection) return null;
  selection.removeAllRanges();
  selection.addRange(range);
  return range;
};

const setCaretFromPoint = (editor, clientX, clientY) => {
  if (!editor) return false;
  const selection = window.getSelection();
  if (!selection) return false;

  let range = null;
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(clientX, clientY);
  } else if (document.caretPositionFromPoint) {
    const pos = document.caretPositionFromPoint(clientX, clientY);
    if (pos) {
      range = document.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.collapse(true);
    }
  }

  if (range && editor.contains(range.commonAncestorContainer)) {
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  }

  return false;
};

const BLOCK_TAGS = ['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'];

const collectReadableText = (node) => {
  if (!node) return '';
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return '';
  if (node.nodeType === Node.ELEMENT_NODE) {
    if (node.tagName === 'BR') return '\n';
    if (node.getAttribute?.('data-dictation-interim') === 'true') return '';
  }
  let text = '';
  node.childNodes.forEach((child) => {
    text += collectReadableText(child);
  });
  if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.includes(node.tagName)) {
    text += '\n';
  }
  return text;
};

const RichTextEditor = forwardRef(({ value, onChange, placeholder = 'Escribe una descripción...', readOnly = false, variables = [], maxHeight, allowImagePaste = true }, ref) => {
  const editorRef = useRef(null);
  const [fontFamily, setFontFamily] = useState('Roboto');
  const [fontSize, setFontSize] = useState('3');
  const [dropIndicator, setDropIndicator] = useState(null);

  const renderVariableChips = useCallback((html) => {
    if (!html) return html;
    let output = html;
    variables.forEach((item) => {
      const key = item.key || item;
      if (!key) return;
      if (output.includes(`data-var="${key}"`)) return;
      const token = `{{${key}}}`;
      const chip = `<span class="var-chip" data-var="${key}" contenteditable="false">${token}</span>`;
      output = output.split(token).join(chip);
    });
    return output;
  }, [variables]);

  useEffect(() => {
    if (!editorRef.current) return;
    const current = editorRef.current.innerHTML;
    if (value !== undefined && value !== current) {
      editorRef.current.innerHTML = renderVariableChips(value || '');
    }
  }, [value, renderVariableChips]);

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    if (readOnly) return;
    onChange?.(editorRef.current.innerHTML);
  }, [onChange, readOnly]);

  const [isDictating, setIsDictating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceMenuAnchor, setVoiceMenuAnchor] = useState(null);
  const { voices: spanishVoices, voiceURI, selectedVoice, setVoiceURI } = useTtsVoice();
  const recognitionRef = useRef(null);
  const isDictatingRef = useRef(false);
  const dictationRangeRef = useRef(null);
  const interimSpanRef = useRef(null);
  const suppressSelectionSyncRef = useRef(false);

  const speechRecognitionSupported = typeof window !== 'undefined' && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  const speechSynthesisSupported = typeof window !== 'undefined' && Boolean(window.speechSynthesis);

  const getEditorSelectionRange = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!editorRef.current || !editorRef.current.contains(range.commonAncestorContainer)) return null;
    return range;
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      if (!isDictatingRef.current || suppressSelectionSyncRef.current) return;
      const range = getEditorSelectionRange();
      if (range && range.collapsed) {
        dictationRangeRef.current = range.cloneRange();
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [getEditorSelectionRange]);

  const ensureRecognition = useCallback(() => {
    if (recognitionRef.current) return recognitionRef.current;
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return null;

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = 'es-CO';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      const editor = editorRef.current;
      if (!editor || readOnly) return;
      editor.focus();

      let finalized = false;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript || '';
        if (!transcript) continue;

        if (!dictationRangeRef.current || !editor.contains(dictationRangeRef.current.startContainer)) {
          dictationRangeRef.current = placeCaretAtEnd(editor);
        }
        const insertionRange = dictationRangeRef.current;
        if (!insertionRange) continue;

        suppressSelectionSyncRef.current = true;

        if (interimSpanRef.current && interimSpanRef.current.parentNode) {
          interimSpanRef.current.parentNode.removeChild(interimSpanRef.current);
          interimSpanRef.current = null;
        }

        const selection = window.getSelection();

        if (result.isFinal) {
          const textNode = document.createTextNode(transcript.trim() ? `${transcript.trim()} ` : '');
          insertionRange.insertNode(textNode);
          const nextRange = document.createRange();
          nextRange.setStartAfter(textNode);
          nextRange.collapse(true);
          dictationRangeRef.current = nextRange;
          selection?.removeAllRanges();
          selection?.addRange(nextRange);
          finalized = true;
        } else {
          const span = document.createElement('span');
          span.setAttribute('data-dictation-interim', 'true');
          span.style.opacity = '0.55';
          span.textContent = transcript;
          const clonedRange = insertionRange.cloneRange();
          clonedRange.insertNode(span);
          interimSpanRef.current = span;
          const afterRange = document.createRange();
          afterRange.setStartAfter(span);
          afterRange.collapse(true);
          selection?.removeAllRanges();
          selection?.addRange(afterRange);
        }

        requestAnimationFrame(() => {
          suppressSelectionSyncRef.current = false;
        });
      }
      if (finalized) emitChange();
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      isDictatingRef.current = false;
      setIsDictating(false);
    };

    recognition.onend = () => {
      if (isDictatingRef.current) {
        try {
          recognition.start();
        } catch (error) {
          // ya estaba iniciado
        }
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [emitChange, readOnly]);

  const toggleDictation = useCallback(() => {
    if (readOnly) return;
    const recognition = ensureRecognition();
    if (!recognition) return;

    if (isDictating) {
      isDictatingRef.current = false;
      setIsDictating(false);
      recognition.stop();
      return;
    }

    const editor = editorRef.current;
    if (editor) {
      editor.focus();
      const currentRange = getEditorSelectionRange();
      dictationRangeRef.current = (currentRange && currentRange.collapsed)
        ? currentRange.cloneRange()
        : placeCaretAtEnd(editor);
    }
    isDictatingRef.current = true;
    setIsDictating(true);
    try {
      recognition.start();
    } catch (error) {
      // ya estaba iniciado
    }
  }, [ensureRecognition, isDictating, readOnly, getEditorSelectionRange]);

  const speakText = useCallback((text) => {
    const started = speakWithVoice(text, { voice: selectedVoice, onEnd: () => setIsSpeaking(false) });
    setIsSpeaking(started);
  }, [selectedVoice]);

  const toggleSpeak = useCallback(() => {
    if (!window.speechSynthesis) return;
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const editor = editorRef.current;
    if (!editor) return;

    const selection = window.getSelection();
    let range = null;
    if (selection && selection.rangeCount > 0) {
      const candidate = selection.getRangeAt(0);
      if (editor.contains(candidate.commonAncestorContainer)) range = candidate;
    }

    let sourceRange;
    if (range && !range.collapsed) {
      sourceRange = range.cloneRange();
    } else {
      sourceRange = document.createRange();
      if (range) {
        sourceRange.setStart(range.startContainer, range.startOffset);
      } else {
        sourceRange.setStart(editor, 0);
      }
      sourceRange.setEnd(editor, editor.childNodes.length);
    }

    speakText(collectReadableText(sourceRange.cloneContents()));
  }, [isSpeaking, speakText]);

  useEffect(() => () => {
    isDictatingRef.current = false;
    recognitionRef.current?.stop();
    window.speechSynthesis?.cancel();
  }, []);

  const handlePaste = useCallback((event) => {
    if (readOnly) return;
    const items = event.clipboardData?.items || [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        if (!allowImagePaste) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          readImage(file, (dataUrl) => insertImage(dataUrl, readOnly));
        }
        return;
      }
    }
  }, [readOnly, allowImagePaste]);

  const insertHtml = useCallback((html) => {
    if (readOnly) return;
    document.execCommand('insertHTML', false, html);
  }, [readOnly]);

  const insertVariable = useCallback((key) => {
    if (!key) return;
    const editor = editorRef.current;
    if (!editor || readOnly) return;

    editor.focus();
    const selection = window.getSelection();
    if (!selection) return;

    let range = null;
    if (selection.rangeCount > 0) {
      const candidate = selection.getRangeAt(0);
      if (editor.contains(candidate.commonAncestorContainer)) {
        range = candidate;
      }
    }
    if (!range) {
      range = placeCaretAtEnd(editor);
      if (!range) return;
    }

    const token = `{{${key}}}`;
    const chip = document.createElement('span');
    chip.className = 'var-chip';
    chip.setAttribute('data-var', key);
    chip.setAttribute('contenteditable', 'false');
    chip.textContent = token;

    const spacer = document.createTextNode(' ');
    range.deleteContents();
    range.insertNode(spacer);
    range.insertNode(chip);

    const nextRange = document.createRange();
    nextRange.setStartAfter(spacer);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);

    emitChange();
  }, [emitChange, readOnly]);

  const removeVariable = useCallback((key) => {
    if (!key || !editorRef.current || readOnly) return;
    const editor = editorRef.current;
    const tokenRegex = new RegExp(`{{\\s*${key.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\s*}}`, 'g');
    const spanRegex = new RegExp(`<span[^>]*data-var="${key.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}"[^>]*>[\\s\\S]*?<\\/span>`, 'g');
    const nextHtml = (editor.innerHTML || '')
      .replace(spanRegex, '')
      .replace(tokenRegex, '');
    editor.innerHTML = nextHtml;
    emitChange();
  }, [emitChange, readOnly]);

  useImperativeHandle(ref, () => ({
    insertVariable,
    removeVariable,
  }));

  const handleDrop = useCallback((event) => {
    if (readOnly) return;
    const variableToken = event.dataTransfer?.getData('text/plain');
    if (variableToken && variableToken.startsWith('{{') && variableToken.endsWith('}}')) {
      event.preventDefault();
      const editor = editorRef.current;
      if (editor) {
        editor.focus();
        const positioned = setCaretFromPoint(editor, event.clientX, event.clientY);
        if (!positioned) {
          placeCaretAtEnd(editor);
        }
      }
      const key = variableToken.replace(/[{}]/g, '');
      insertVariable(key);
      setDropIndicator(null);
      return;
    }
    const files = event.dataTransfer?.files || [];
    const image = Array.from(files).find((file) => file.type.startsWith('image/'));
    if (image) {
      event.preventDefault();
      if (allowImagePaste) {
        readImage(image, (dataUrl) => insertImage(dataUrl, readOnly));
      }
    }
    setDropIndicator(null);
  }, [readOnly, insertVariable, allowImagePaste]);

  const handleDragOver = useCallback((event) => {
    if (readOnly) return;
    const variableToken = event.dataTransfer?.getData('text/plain');
    if (!variableToken || !variableToken.startsWith('{{') || !variableToken.endsWith('}}')) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';

    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const positioned = setCaretFromPoint(editor, event.clientX, event.clientY);
    if (!positioned) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0).cloneRange();
    if (!editor.contains(range.commonAncestorContainer)) return;
    range.collapse(true);

    let rect = range.getBoundingClientRect();
    if (!rect || (!rect.height && !rect.width)) {
      const marker = document.createElement('span');
      marker.textContent = '\u200b';
      range.insertNode(marker);
      rect = marker.getBoundingClientRect();
      marker.parentNode?.removeChild(marker);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    const editorRect = editor.getBoundingClientRect();
    const top = rect.top - editorRect.top;
    const left = rect.left - editorRect.left;
    const height = Math.max(14, rect.height || 16);
    setDropIndicator({
      top: Math.max(4, top),
      left: Math.max(4, left),
      height,
    });
  }, [readOnly]);

  const clearDropIndicator = useCallback(() => {
    setDropIndicator(null);
  }, []);

  const handleFontColorApply = useCallback((color) => {
    applyCommand('foreColor', color, readOnly);
  }, [readOnly]);

  const handleFontBackgroundColorApply = useCallback((color) => {
    applyTextBackgroundColor(color, readOnly);
  }, [readOnly]);

  const handleEditorDoubleClick = useCallback((event) => {
    if (readOnly) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.classList.contains('var-chip')) return;
    target.remove();
    emitChange();
  }, [emitChange, readOnly]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Select
          size="small"
          value={fontFamily}
          onChange={(event) => {
            const value = event.target.value;
            setFontFamily(value);
            applyCommand('fontName', value, readOnly);
          }}
          sx={{ minWidth: 140, height: 32 }}
          disabled={readOnly}
        >
          {FONT_OPTIONS.map((font) => (
            <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
              {font}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={fontSize}
          onChange={(event) => {
            const value = event.target.value;
            setFontSize(value);
            applyCommand('fontSize', value, readOnly);
          }}
          sx={{ minWidth: 90, height: 32 }}
          disabled={readOnly}
        >
          <MenuItem value="1">12 px</MenuItem>
          <MenuItem value="2">13 px</MenuItem>
          <MenuItem value="3">14 px</MenuItem>
          <MenuItem value="4">16 px</MenuItem>
          <MenuItem value="5">18 px</MenuItem>
          <MenuItem value="6">22 px</MenuItem>
          <MenuItem value="7">28 px</MenuItem>
        </Select>
        <RichTextColorButton
          title="Color de fuente"
          icon={<FormatColorText fontSize="small" />}
          onApply={handleFontColorApply}
          disabled={readOnly}
        />
        <RichTextColorButton
          title="Color de fondo de texto"
          icon={<FormatColorFill fontSize="small" />}
          onApply={handleFontBackgroundColorApply}
          disabled={readOnly}
        />
        <Divider flexItem orientation="vertical" />
        <Tooltip title="Deshacer">
          <IconButton size="small" onClick={() => applyCommand('undo', null, readOnly)} disabled={readOnly}>
            <Undo fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Rehacer">
          <IconButton size="small" onClick={() => applyCommand('redo', null, readOnly)} disabled={readOnly}>
            <Redo fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem orientation="vertical" />
        <Tooltip title="Negrita">
          <IconButton size="small" onClick={() => applyCommand('bold', null, readOnly)} disabled={readOnly}>
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cursiva">
          <IconButton size="small" onClick={() => applyCommand('italic', null, readOnly)} disabled={readOnly}>
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Subrayado">
          <IconButton size="small" onClick={() => applyCommand('underline', null, readOnly)} disabled={readOnly}>
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Tachado">
          <IconButton size="small" onClick={() => applyCommand('strikeThrough', null, readOnly)} disabled={readOnly}>
            <StrikethroughS fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Limpiar formato">
          <IconButton size="small" onClick={() => applyCommand('removeFormat', null, readOnly)} disabled={readOnly}>
            <FormatClear fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem orientation="vertical" />
        <Tooltip title="Lista">
          <IconButton size="small" onClick={() => applyCommand('insertUnorderedList', null, readOnly)} disabled={readOnly}>
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista numerada">
          <IconButton size="small" onClick={() => applyCommand('insertOrderedList', null, readOnly)} disabled={readOnly}>
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem orientation="vertical" />
        <Tooltip title="Alinear izquierda">
          <IconButton size="small" onClick={() => applyCommand('justifyLeft', null, readOnly)} disabled={readOnly}>
            <FormatAlignLeft fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Centrar">
          <IconButton size="small" onClick={() => applyCommand('justifyCenter', null, readOnly)} disabled={readOnly}>
            <FormatAlignCenter fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinear derecha">
          <IconButton size="small" onClick={() => applyCommand('justifyRight', null, readOnly)} disabled={readOnly}>
            <FormatAlignRight fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem orientation="vertical" />
        <Tooltip title="Insertar enlace">
          <IconButton
            size="small"
            onClick={() => {
              if (readOnly) return;
              const url = window.prompt('URL del enlace');
              if (url) applyCommand('createLink', url, readOnly);
            }}
            disabled={readOnly}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Quitar enlace">
          <IconButton size="small" onClick={() => applyCommand('unlink', null, readOnly)} disabled={readOnly}>
            <LinkOff fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider flexItem orientation="vertical" />
        <Tooltip title={
          !speechRecognitionSupported
            ? 'Dictado por voz no soportado en este navegador'
            : (isDictating ? 'Detener dictado' : 'Dictar por voz')
        }>
          <span>
            <IconButton
              size="medium"
              onClick={toggleDictation}
              disabled={readOnly || !speechRecognitionSupported}
              sx={micButtonSx(isDictating)}
            >
              {isDictating ? <Mic fontSize="small" /> : <MicOff fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={
          !speechSynthesisSupported
            ? 'Lectura en voz alta no soportada en este navegador'
            : (isSpeaking ? 'Detener lectura' : 'Leer en voz alta (todo, lo resaltado o desde el cursor)')
        }>
          <span>
            <IconButton
              size="medium"
              onClick={toggleSpeak}
              disabled={!speechSynthesisSupported}
              sx={speakerButtonSx(isSpeaking)}
            >
              {isSpeaking ? <Stop fontSize="small" /> : <VolumeUp fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Configurar voz de lectura en español">
          <span>
            <IconButton
              size="small"
              onClick={(event) => setVoiceMenuAnchor(event.currentTarget)}
              disabled={!speechSynthesisSupported}
            >
              <RecordVoiceOver fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Popover
          open={Boolean(voiceMenuAnchor)}
          anchorEl={voiceMenuAnchor}
          onClose={() => setVoiceMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Stack spacing={1.5} sx={{ p: 2, width: 320 }}>
            <Typography variant="subtitle2">Voz de lectura</Typography>
            <Select
              size="small"
              value={spanishVoices.some((voice) => voice.voiceURI === voiceURI) ? voiceURI : ''}
              onChange={(event) => setVoiceURI(event.target.value)}
              displayEmpty
            >
              <MenuItem value="">Voz predeterminada del sistema</MenuItem>
              {spanishVoices.map((voice) => (
                <MenuItem key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.name} ({voice.lang})
                </MenuItem>
              ))}
            </Select>
            {spanishVoices.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No se encontraron voces en español instaladas en este navegador/sistema.
              </Typography>
            )}
            <Box>
              <IconButton
                size="small"
                onClick={() => speakWithVoice(VOICE_TEST_TEXT, { voice: selectedVoice })}
                sx={speakerButtonSx(false)}
              >
                <VolumeUp fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Probar voz
              </Typography>
            </Box>
          </Stack>
        </Popover>
      </Stack>
      <Box sx={{ position: 'relative', mt: 1 }}>
        <Box
          ref={editorRef}
          contentEditable
          onInput={emitChange}
          onBlur={emitChange}
          onPaste={handlePaste}
          onDragOver={handleDragOver}
          onDragLeave={clearDropIndicator}
          onDrop={handleDrop}
          onDoubleClick={handleEditorDoubleClick}
          suppressContentEditableWarning
          sx={{
            minHeight: 140,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 1.5,
            typography: 'body2',
            outline: 'none',
            '&:empty:before': {
              content: `"${placeholder}"`,
              color: 'text.disabled',
            },
            '& img': {
              maxWidth: '100%',
              borderRadius: 1,
              display: 'block',
              my: 1,
            },
            '& .var-chip': {
              display: 'inline-block',
              px: 0.75,
              py: 0.25,
              mr: 0.5,
              borderRadius: 1,
              backgroundColor: 'rgba(0, 150, 136, 0.12)',
              color: 'text.primary',
              fontSize: '0.75rem',
              border: '1px solid',
              borderColor: 'divider',
              userSelect: 'all',
            },
            '& ul, & ol': {
              paddingLeft: 3,
              marginLeft: 0,
            },
            '& li': {
              marginLeft: 0,
              paddingLeft: 0,
            },
            ...(maxHeight && { maxHeight, overflowY: 'auto' }),
            ...(readOnly && { pointerEvents: 'none', opacity: 0.7 }),
          }}
        />
        {dropIndicator && !readOnly && (
          <Box
            sx={{
              position: 'absolute',
              top: dropIndicator.top,
              left: dropIndicator.left,
              width: 2,
              height: dropIndicator.height,
              backgroundColor: 'primary.main',
              borderRadius: 2,
              pointerEvents: 'none',
              boxShadow: '0 0 0 1px rgba(25,118,210,0.15)',
            }}
          />
        )}
      </Box>
    </Box>
  );
});

export { collectReadableText };
export default RichTextEditor;
